import { z } from "zod";

/**
 * Validates API response data against a Zod schema.
 * Uses fail-soft approach: logs errors but returns raw data if validation fails.
 *
 * @param schema - Zod schema to validate against
 * @param data - Raw data from API response
 * @param context - Description of the API call for logging
 * @returns Validated and typed data
 */
export function parseApiResponse<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context: string
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    // Log detailed error for debugging as warning to avoid triggering Next.js error overlay
    console.warn(`[API Validation] ${context} - Validation failed:`, {
      errors: result.error.errors.map(e => `${e.path.join(".")}: ${e.message}`),
      receivedData: data,
    });

    // TODO: Add Sentry/monitoring integration here
    // captureException(new Error(`API validation failed: ${context}`), {
    //   extra: { errors: result.error.format(), data }
    // });

    // Fail-soft: return raw data cast to expected type
    return data as T;
  }

  return result.data;
}

/**
 * Validates API response and throws if invalid.
 * Use this when you want strict validation that fails the request.
 *
 * @param schema - Zod schema to validate against
 * @param data - Raw data from API response
 * @param context - Description of the API call for error messages
 * @returns Validated and typed data
 * @throws Error if validation fails
 */
export function parseApiResponseStrict<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context: string
): T {
  const result = schema.safeParse(data);

  if (!result.success) {
    const errorMessage = result.error.errors
      .map((e) => `${e.path.join(".")}: ${e.message}`)
      .join(", ");

    throw new Error(`API validation failed for ${context}: ${errorMessage}`);
  }

  return result.data;
}

/**
 * Validates an array of items from API response.
 * Filters out invalid items and logs errors for each.
 *
 * @param schema - Zod schema for individual items
 * @param data - Array of raw items
 * @param context - Description of the API call for logging
 * @returns Array of valid items only
 */
export function parseApiArray<T>(
  schema: z.ZodType<T>,
  data: unknown[],
  context: string
): T[] {
  const validItems: T[] = [];

  for (let i = 0; i < data.length; i++) {
    const result = schema.safeParse(data[i]);

    if (result.success) {
      validItems.push(result.data);
    } else {
      console.warn(`[API Validation] ${context} - Invalid item at index ${i}:`, {
        errors: result.error.format(),
        item: data[i],
      });
    }
  }

  return validItems;
}

/**
 * Creates a typed API response parser for a specific schema.
 * Useful for creating reusable parsers for specific endpoints.
 *
 * @param schema - Zod schema to validate against
 * @param context - Description of the API call for logging
 * @returns Parser function that validates data
 */
export function createApiParser<T>(schema: z.ZodType<T>, context: string) {
  return (data: unknown): T => parseApiResponse(schema, data, context);
}
